import React, {FormEvent, useEffect, useState} from "react";
import '../../css/components/NewPlan.css';
import {useMultistepForm} from "./components/utils/form/useMultistepForm";
import {
    Attachment,
    Exercise,
    FailedExercise,
    GroupTrainingAttendance,
    Note,
    Participant,
    ParticipantNoteContent,
    Plan,
    PlanExercise,
    ReworkPlanFormData,
    Stage
} from "../../types/domain";
import {useLocation, useNavigate} from 'react-router-dom';
import CustomModal from "./components/utils/modal/CustomModal";
import {GeneralForm} from "./components/reworkPlanSteps/GeneralForm";
import {ExercisesForm} from "./components/reworkPlanSteps/ExercisesForm";
import StateService from "../../services/state.service";
import GroupTrainingAttendanceService from "../../services/grouptrainingattendance.service";
import PlanService from "../../services/plan.service";
import {Button, LoadingOverlay} from "@mantine/core";
import AppHeader from "../header/AppHeader";
import {Container} from "react-bootstrap";
import {NotesForm} from "./components/reworkPlanSteps/NotesForm";
import Cookies from "js-cookie";
import TopicService from "../../services/topic.service";
import * as Sentry from "@sentry/react";
import {ModalType} from "../../types/UITypes";
import StageService from "../../services/stage.service";
import {planExercisesNotInFailedExercises} from "./components/utils/helperFunctions";

/**
 * Rework plan page
 * @author KMU
 */

const INITIAL_DATA: ReworkPlanFormData = {
    topic: {
        id: 0,
        name: ""
    },
    stages: [],
    serviceCenter: {
        id: 0,
        name: ""
    },
    trainingGroup: null,
    participant: null,
    date: "",
    prepTimeContent: 0,
    prepTimeOrg: 0,
    duration: 0,
    timeSupport: 0,
    planExercises: [],
    newExercises: [],
    newAttachments: [],
    trainingAttendances: [],
    planNoteContent: "",
    groupNoteContent: "",
    participantNoteContents: [],
    notes: [],
    user: {
        id: 0,
        username: "",
        role: null,
        serviceCenters: [],
        active: true,
        firstname: "",
        lastname: "",
        password: ""
    },
    creationDate: ""
}

export default function ReworkPlan() {
    const navigate = useNavigate();

    //state that contains the id of the plan that will be reworked
    const state = useLocation();

    //form data mapped to a reworked plan
    const [data, setData] = useState(INITIAL_DATA)
    const [dbStages, setDbStages] = useState<Array<Stage>>([]);
    const [modal, setModal] = useState<ModalType>({
        show: false,
        title: "",
        body: "",
        decline: false,
        handleOk: (() => {
        }),
        handleCancel: (() => {
        })
    })

    //plan that will be reworked
    const [plan, setPlan] = useState<Plan>({
        id: 0,
        date: data.date,
        duration: data.duration,
        prepTimeContent: data.prepTimeContent,
        prepTimeOrg: data.prepTimeOrg,
        timeSupport: data.timeSupport,
        exercises: null,
        stages: data.stages,
        state: {id: 0, name: ""},
        topic: {id: 0, name: ""},
        group: data.trainingGroup,
        participant: null,
        notes: data.notes,
        user: data.user!,
        creationDate: data.creationDate
    });

    const [isSaving, setIsSaving] = useState(false);
    const [failedExercises, setFailedExercises] = useState<FailedExercise[]>([]);

    /*Checks access of user*/
    useEffect(() => {
        if (!Cookies.get('username')) {
            navigate('/login', {replace: true})
        }
    });

    useEffect(() => {
        StageService.getAll().then((response: any) => {
            let loadedStages = response.data;
            if (loadedStages) {
                const prophStage = loadedStages.pop();
                loadedStages.unshift(prophStage);
            }
            setDbStages(loadedStages);
        }).catch((e: Error) => console.log(e));
    }, []);

    /**
     * Helper function to update the data fields
     * @param fields fields with new values
     */
    function updateFields(fields: Partial<any>) {
        setData(prev => {
            return {...prev, ...fields}
        })
    }

    //attribute to navigate between the form pages
    const {
        step,
        currentStepIndex,
        isFirstStep,
        isLastStep, back, next
    } =
        useMultistepForm([
            <GeneralForm {...data} updateFields={updateFields}/>,
            <ExercisesForm {...data} updateFields={updateFields} failedExercises={failedExercises}
                           dbStages={dbStages}/>,
            <NotesForm {...data} updateFields={updateFields}/>,
        ])

    //get data for plan with the given id from the database
    useEffect(() => {
        const dbPlan = state.state;
        if (dbPlan != null) {
            setPlan(dbPlan);

            //init group training attendances entries
            let attendances: Array<GroupTrainingAttendance> = [];
            let participantNotes: Array<ParticipantNoteContent> = [];
            let groupNoteContent: string | null = null;    //only when group training needed

            if (dbPlan.group != null) {
                data.serviceCenter = dbPlan.group.serviceCenter
                groupNoteContent = "";

                dbPlan.group.participants.forEach((participant: Participant) => {
                    attendances.push({
                        id: 0,
                        participant: participant,
                        attended: true
                    })

                    participantNotes.push({
                        participant: participant,
                        content: ""
                    })
                })
            } else if (dbPlan.participant != null) {
                data.serviceCenter = dbPlan.participant.serviceCenter
                participantNotes.push({
                    participant: dbPlan.participant,
                    content: ""
                })
            }

            setData({
                ...data,
                topic: dbPlan.topic,
                stages: dbPlan.stages,
                trainingGroup: dbPlan.group,
                participant: dbPlan.participant,
                date: dbPlan.date,
                prepTimeOrg: dbPlan.prepTimeOrg,
                prepTimeContent: dbPlan.prepTimeContent,
                timeSupport: dbPlan.timeSupport,
                duration: dbPlan.duration,
                planExercises: dbPlan.exercises.sort((a: PlanExercise, b: PlanExercise) => a.position! - b.position!),
                trainingAttendances: attendances,
                groupNoteContent: groupNoteContent,
                participantNoteContents: participantNotes,
                user: dbPlan.user
            })
        }

        // Reset newExercises list for exercises form
        updateFields({newExercises: [], newAttachments: []})
    }, [])

    /**
     * Handler function to navigate back to the plan overview page
     */
    const handleBackToOverview = () => setModal({
        ...modal,
        show: true,
        title: "Hinweis",
        body: "Achtung, jegliche nicht gespeicherten Eingaben gehen verloren!",
        decline: true,
        handleOk: () => {
            setModal({...modal, show: false});

            //reset data
            setData(INITIAL_DATA);
            updateFields({newExercises: [], newAttachments: []})
            const dbPlan = state.state;
            updateFields({planExercises: dbPlan.exercises})

            navigate('/plans', {replace: true})
        },
        handleCancel: () => setModal({...modal, show: false})
    });

    /**
     * Helper function to show modal with error message
     */
    function showErrorModal(message: string) {
        // show error modal and stay on page
        setModal({
            ...modal,
            show: true,
            title: "Fehler",
            body: message,
            decline: true,
            handleOk: () => {
                setModal({...modal, show: false});
            },
            handleCancel: () => {
                setModal({...modal, show: false});
                navigate('/plans', {replace: true})
            },
            okText: "Zurück zur Nachbearbeitung",
            cancelText: "Abbrechen und zur Übersicht"
        })
    }

    function showPartialFailureModal() {
        setModal({
            ...modal,
            show: true,
            title: "Fehler beim Speichern",
            body: "Einige Übungen konnten nicht gespeichert werden!",
            decline: true,
            handleOk: () => setModal({...modal, show: false}),
            handleCancel: () => {
                setModal({...modal, show: false});
                navigate('/plans', {replace: true});
            },
            okText: "Zur Planung",
            cancelText: "Abbrechen und zur Übersicht"
        })
    }

    /**
     * Set and reset exercises and attachments after a partial successful
     * create or update call
     * @param result result of the create or update call
     */
    function handlePartialFailure(result: any) {
        if (result.failedExercises?.length > 0) {       // Exercise failure
            setFailedExercises(result.failedExercises);
            const failedExerciseObjects: Array<Exercise> = result.failedExercises.map((f: FailedExercise) => f.exercise);

            const savedPlanExercises: Array<PlanExercise> = planExercisesNotInFailedExercises(result.plan?.exercises ?? [], result.failedExercises)
                .sort((a: PlanExercise, b: PlanExercise) => a.position! - b.position!) ?? [];

            // wrap failed exercises back into PlanExercises
            let pos = savedPlanExercises.length + 1;
            const failedPlanExercises: Array<PlanExercise> = failedExerciseObjects.map(exe => {
                // Recover the user's selected attachment subset from planExercises
                const original = data.planExercises?.find(pe =>
                    pe.exercise.name === exe.name &&
                    pe.exercise.description === exe.description &&
                    pe.exercise.material === exe.material
                );
                return {
                    id: 0,
                    exercise: exe,
                    attachments: (original?.attachments ?? []).filter(a => a.id && a.id > 0),
                    execution: 'Ja',        // rework table HAS execution column — default to 'Ja'
                    interest: undefined,
                    position: pos++
                };
            });

            // only keep attachments for exercises that failed - other ones were saved in database
            const remainingAttachments = data.newAttachments.filter(a =>
                failedExerciseObjects.some((f: Exercise) =>
                    f.name === a.exercise.name &&
                    f.description === a.exercise.description &&
                    f.material === a.exercise.material
                )
            );

            setData({
                ...data,
                newExercises: [...savedPlanExercises, ...failedPlanExercises],
                newAttachments: remainingAttachments
            });

            showPartialFailureModal();
            return;
        } else {        //Plan failure
            showErrorModal("Checken Sie die allgemeinen Planinformationen und versuchen Sie es erneut.");
            return;
        }
    }


    /**
     * Creates notes based on the note-strings stored in the data
     */
    function getNotes() {
        //create notes
        let notes: Array<Note> = [];
        if (data.planNoteContent.trim() !== "") {
            notes.push({
                id: 0,
                group: null,
                participant: null,
                content: data.planNoteContent
            })
        }

        if (data.groupNoteContent && data.groupNoteContent.trim() !== "") {
            notes.push({
                id: 0,
                group: plan.group,
                participant: null,
                content: data.groupNoteContent
            })
        }

        data.participantNoteContents.forEach(note => {
            if (note.content.trim() !== "") {
                notes.push({
                    id: 0,
                    group: null,
                    participant: note.participant,
                    content: note.content
                })
            }
        });
        return notes
    }

    /**
     * Save reworked plan
     * @param e form event
     */
    async function onSubmit(e: FormEvent) {
        e.preventDefault()

        if (isFirstStep) {
            if (data.duration == 0) {
                setModal({
                    ...modal,
                    show: true,
                    title: "Fehlermeldung",
                    body: "Die Trainingsdauer kann nicht 0 sein!",
                    decline: false,
                    handleOk: () => {
                        setModal({...modal, show: false});
                    },
                    handleCancel: () => {
                    }
                })
            } else {
                return next();
            }
        } else {
            //check if all exercises that were executed also have an interest filled in
            let missingRework = false;
            for (let i in data.planExercises) {
                let planExe = data.planExercises[parseInt(i)];
                missingRework = planExe.execution === null || planExe.execution === undefined ||
                    (planExe.execution === "Ja" && (planExe.interest === null || planExe.interest === undefined || planExe.interest === 0))
                if (missingRework) break;
            }

            //only if all exercises were reworked the plan can be updated
            if (missingRework) {
                setModal({
                    ...modal,
                    show: true,
                    title: "Fehlermeldung",
                    body: "Es müssen alle Übungen nachbearbeitet werden!",
                    decline: false,
                    handleOk: () => {
                        setModal({...modal, show: false});
                    },
                    handleCancel: () => {
                    }
                });
                return;
            }

            // is not the last step
            if (currentStepIndex === 1) {
                // only proceed if there are new exercises with attachments to save
                if (data.newAttachments.length === 0) {
                    return next();
                }
            }

            setIsSaving(true);
            setFailedExercises([]);

            // is last step -> save notes & attendances
            if (currentStepIndex !== 1) {
                //set remaining attributes
                plan.state = (await StateService.getByName("abgeschlossen")).data;
                plan.exercises = data.planExercises;
                plan.duration = data.duration;
                plan.prepTimeContent = data.prepTimeContent;
                plan.prepTimeOrg = data.prepTimeOrg;
                plan.timeSupport = data.timeSupport;

                let saved = await GroupTrainingAttendanceService.createCollection(data.trainingAttendances, plan)
                    .catch((e: Error) => {
                        Sentry.captureException(e, {
                            extra: {
                                request: {
                                    attendances: JSON.parse(JSON.stringify(data.trainingAttendances))
                                },
                            },
                        });
                        setIsSaving(false);
                        showErrorModal("Fehler beim Speichern der Anwesenheiten. Versuchen Sie es erneut oder kontaktieren Sie den Administrator.");
                        return null;
                    });

                if (saved !== null) {
                    plan.notes = getNotes()
                }
            }

            const formData = new FormData();

            //create attachments
            for (const exeAttachment of data.newAttachments) {
                const exe = exeAttachment.exercise; //attachment exercise
                const exerciseIndex = data.newExercises.findIndex(e => e.exercise.name === exe.name
                    && e.exercise.description === exe.description && e.exercise.material === exe.material);


                let newExeAttachments: Array<Attachment> = [];

                // find the matching planExercise and push attachments
                const matchingPlanExe = data.planExercises?.find(pe =>
                    pe.exercise.name === exe.name &&
                    pe.exercise.description === exe.description &&
                    pe.exercise.material === exe.material
                );

                // the new exercise added during rework lives in newExercises
                // this is what gets serialized in the payload
                const newExe = data.newExercises.find(e =>
                    e.exercise.name === exe.name &&
                    e.exercise.description === exe.description &&
                    e.exercise.material === exe.material
                );

                for (const a of exeAttachment.attachments) {
                    let attachmentTopic = plan.topic;
                    if (!a.isTopicSpecific) {
                        attachmentTopic = (await TopicService.getByName("ALLGEMEIN")).data; //attachment is not topic specific
                    }
                    a.topic = attachmentTopic.name;

                    // resolve solution path for this specific Arbeitsblatt
                    let resolvedSolutionPath = "";
                    if (a.type.name === 'Arbeitsblatt' && a.solutionFile) {
                        resolvedSolutionPath = `${attachmentTopic.name}/${a.solutionFile.name}`;
                    }

                    newExeAttachments.push({
                        id: 0,
                        attachmentType: a.type,
                        path: a.name,
                        solutionPath: resolvedSolutionPath,
                        stages: a.stages?.length > 0 ? a.stages : (matchingPlanExe?.exercise.stages ?? data.stages),
                        topic: attachmentTopic
                    });

                    // Upload the main file
                    const adaptedFile = new File([a.file!], a.file!.name, {type: a.file!.type});
                    formData.append(`files[${exerciseIndex}]`, adaptedFile);

                    // Upload paired Lösungsblatt if present
                    if (a.type.name === 'Arbeitsblatt' && a.solutionFile) {
                        const adaptedSolution = new File([a.solutionFile], a.solutionFile.name, {type: a.solutionFile.type});
                        formData.append(`files[${exerciseIndex}]`, adaptedSolution);
                    }
                }

                if (newExe) {
                    // remove unsaved attachments from PlanExercise
                    newExe.attachments = newExe.attachments.filter(a => a.id && a.id > 0);
                    newExe.exercise.attachments.push(...newExeAttachments);
                }
            }

            // set user on new exercises
            data.newExercises.forEach(newExe => {
                if (!newExe.exercise.user) newExe.exercise.user = plan.user;
                const planExe = data.planExercises?.find(pe =>
                    pe.exercise.name === newExe.exercise.name &&
                    pe.exercise.description === newExe.exercise.description &&
                    pe.exercise.material === newExe.exercise.material
                );
                if (planExe) {
                    const selectedSaved = planExe.attachments.filter(a => a.id && a.id > 0);
                    const newUploads = newExe.attachments.filter(a => !a.id || a.id === 0);
                    newExe.attachments = [...selectedSaved, ...newUploads];
                }
            });
            plan.exercises = data.planExercises

            formData.append("plan", new Blob(
                [JSON.stringify(plan)],
                {type: "application/json"}
            ));

            await PlanService.updateWholePlanWithAttachments(formData)
                .then(async (response: any) => {
                    const result = response.data;
                    if (response.status === 200) {
                        // was not the last step
                        if (currentStepIndex === 1) {
                            // update planExercises with saved exercises
                            const savedPlanExercises: Array<PlanExercise> = result.plan?.exercises
                                ?.sort((a: PlanExercise, b: PlanExercise) => a.position! - b.position!) ?? [];
                            setPlan(result.plan);
                            updateFields({
                                newExercises: [],
                                newAttachments: [],
                                planExercises: savedPlanExercises
                            });
                            setIsSaving(false);
                            next();
                        } else {
                            //reset data
                            setData(INITIAL_DATA);
                            updateFields({newExercises: [], newAttachments: []})
                            setIsSaving(false);

                            setModal({
                                ...modal,
                                show: true,
                                title: "Hinweis",
                                body: "Der neue Plan wurde erfolgreich nachbearbeitet!",
                                decline: false,
                                handleOk: () => {
                                    setModal({...modal, show: false});
                                    navigate('/plans', {replace: true})
                                },
                                handleCancel: () => {
                                }
                            })
                        }
                    } else if (response.status === 207) {
                        Sentry.captureException(new Error("Partially failed to save exercises"), {
                            extra: {
                                request: {
                                    error: "partial",
                                    newPlan: JSON.parse(JSON.stringify(plan)),
                                    exercises: JSON.parse(JSON.stringify(data.newExercises)),
                                    attachments: JSON.parse(JSON.stringify(data.newAttachments)),
                                    failedExercises: JSON.parse(JSON.stringify(result.failedExercises))
                                },
                            },
                        });
                        handlePartialFailure(result);
                        // stay on exercises step — don't call next()
                    } else {
                        Sentry.captureException(new Error("General plan saving error"), {
                            extra: {
                                request: {
                                    error: "general",
                                    newPlan: JSON.parse(JSON.stringify(plan)),
                                    exercises: JSON.parse(JSON.stringify(data.newExercises)),
                                    attachments: JSON.parse(JSON.stringify(data.newAttachments))
                                },
                            },
                        });
                        showErrorModal("Allgemeiner Speicherfehler. Versuchen Sie es erneut oder kontaktieren Sie den Administrator.");
                    }
                })
                .catch((e: Error) => {
                    Sentry.captureException(e, {
                        extra: {
                            request: {
                                plan: JSON.parse(JSON.stringify(plan)),
                            },
                        },
                    });
                    setIsSaving(false);

                    if (currentStepIndex === 1) {
                        showErrorModal("Allgemeiner Speicherfehler. Versuchen Sie es erneut oder kontaktieren Sie den Administrator.");
                    } else {
                        showErrorModal("Fehler beim Speichern der Notizen. Versuchen Sie es erneut oder kontaktieren Sie den Administrator.");
                    }
                });

            setIsSaving(false);

        }
    }

    return (
        <div>
            <AppHeader showStartpageIcon={true} showNewPlanIcon={false}
                       showUserIcon={true} showHomeIcon={false} showBigLogo={false}/>
            <Container className="app-content-container">
                <form onSubmit={onSubmit}
                      onKeyDown={(e: React.KeyboardEvent<HTMLFormElement>) => {
                          if (e.key === "Enter") {
                              e.preventDefault();
                          }
                      }}
                >
                    {step}
                    <div className="buttonRow">
                        <Button onClick={isFirstStep ? handleBackToOverview : back}>
                            {isFirstStep ? "Zurück zur Übersicht" : "Zurück"}
                        </Button>
                        <Button type="submit" disabled={isSaving}>{isLastStep ? "Speichern" : "Weiter"}</Button>
                    </div>
                </form>
                <CustomModal title={modal.title}
                             body={modal.body}
                             decline={modal.decline} showModal={modal.show} handleOk={modal.handleOk}
                             handleCancel={modal.handleCancel}
                />
            </Container>
            <LoadingOverlay
                className="fullscreen-overlay"
                visible={isSaving}
                loaderProps={{size: 'lg', color: 'blueish'}}
                overlayOpacity={0.5}
                overlayColor="#c5c5c5"
                overlayBlur={3}
            />
        </div>
    )
}